import { z } from 'zod';
import type { UseFormRegister, UseFormWatch, UseFormSetValue, FieldErrors } from 'react-hook-form';
import { emailSchema, emailOptionalSchema } from '@/schemas/shared/contactSchema';


/** Nullable non-negative integer field (user-typed numeric inputs) */
const nonnegativeIntOrNull = z.number().int().min(0, 'attendance:configuration.validation.nonnegativeInt').nullable();

/** Nullable positive integer field (minimum 1, zero not allowed) */
const positiveIntOrNull = z.number().int().min(1, 'attendance:configuration.validation.positiveInt').nullable();

export const attendanceConfigurationSchema = z.object({
  // General - Marcajes
  geoOfflineRequired: z.boolean(),
  collationMarkings: z.boolean(),
  geoWebMarking: z.boolean(),
  disableGeoMarking: z.boolean(),
  geoInconsistencyAlert: z.boolean(),
  employeeMarkingApproval: z.boolean(),
  approvalDeadlineHours: nonnegativeIntOrNull,
  todayMarkingModification: z.boolean(),

  // General - Eventos
  eventDefinitionMethod: z.string(),
  entryEvent: z.number().nullable(),
  exitEvent: z.number().nullable(),
  collationTolerance: nonnegativeIntOrNull,
  collationEntryEvent: z.number().nullable(),
  collationExitEvent: z.number().nullable(),

  // Cálculo - Cortes Mensuales
  multiCutsSameMonth: z.boolean(),
  sendByDateConcept: z.boolean(),
  groupClosure: z.boolean(),

  // Cálculo - Condiciones
  holidaysAsWorkdays: z.boolean(),
  art38IgnoreHolidays: z.boolean(),
  calendarScheduleManagement: z.boolean(),
  halfDayAbsenteeism: z.string(),
  calculationByContractEnd: z.boolean(),

  // Notificaciones - Tolerancia
  entryAbsenceMinutes: nonnegativeIntOrNull,
  exitAbsenceMinutes: nonnegativeIntOrNull,
  repeatedMarkingWait: nonnegativeIntOrNull,
  absenceValidationControls: z.array(z.number()),

  // Notificaciones - Notificaciones
  notifyAbsenceEmployee: z.boolean(),
  notifyAbsenceSupervisor: z.boolean(),
  notifyNoTimeControl: z.boolean(),
  notifyAbsenceAdmin: z.boolean(),
  adminAbsenceEmail: z.string(),
  notifyDisconnectedDevice: z.boolean(),
  disconnectedDeviceEmail: z.string(),
  markingIncidentRecipient: z.number().nullable(),
  notifyEntryDelay: z.boolean(),
  entryDelayToleranceMinutes: positiveIntOrNull,
  notifyExitDelay: z.boolean(),
  exitDelayToleranceMinutes: positiveIntOrNull,
  contractCompanyAbsence: z.boolean(),
  massAttendanceEmail: z.string(),
  dtFiscalizationEmails: z.string(),
  excessHoursEmail: z.string(),

  // Validaciones - Gestor de Marcas
  autoFillHolidays: z.boolean(),
  autoFillVacations: z.boolean(),
  autoFillSickLeave: z.boolean(),
  autoFillSpecialPermissions: z.boolean(),
  autoFillAdminPermissions: z.boolean(),
  autoFillFractionPermissions: z.boolean(),

  // Validaciones - Horas Extras
  approvalFlow: z.boolean(),
  reviewerProfiles: z.array(z.number()),
  approverProfiles: z.array(z.number()),

  // Validaciones - Pacto Compensación
  automaticMode: z.string(),
  cutDaysSlack: nonnegativeIntOrNull,
}).superRefine((data, ctx) => {
  // approvalDeadlineHours requerido cuando employeeMarkingApproval está activo
  if (data.employeeMarkingApproval && data.approvalDeadlineHours === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['approvalDeadlineHours'],
      message: 'attendance:configuration.validation.approvalDeadlineRequired',
    });
  }

  // notifyAbsenceAdmin requerido cuando el modo automático está habilitado
  if (data.automaticMode !== '' && !data.notifyAbsenceAdmin) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['notifyAbsenceAdmin'],
      message: 'attendance:configuration.validation.notifyAbsenceAdminRequired',
    });
  }

  // adminAbsenceEmail requerido + email cuando notifyAbsenceAdmin o automaticMode activo
  const adminEmailRequired = data.notifyAbsenceAdmin || data.automaticMode !== '';
  if (adminEmailRequired) {
    const emailValidation = emailSchema.safeParse(data.adminAbsenceEmail);
    if (!emailValidation.success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['adminAbsenceEmail'],
        message: emailValidation.error.errors[0].message,
      });
    }
  }

  // disconnectedDeviceEmail requerido + email cuando notifyDisconnectedDevice está activo
  if (data.notifyDisconnectedDevice) {
    const emailValidation = emailSchema.safeParse(data.disconnectedDeviceEmail);
    if (!emailValidation.success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['disconnectedDeviceEmail'],
        message: emailValidation.error.errors[0].message,
      });
    }
  }

  // massAttendanceEmail: formato válido si no está vacío
  if (data.massAttendanceEmail.trim() !== '') {
    const emailValidation = emailOptionalSchema.safeParse(data.massAttendanceEmail);
    if (!emailValidation.success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['massAttendanceEmail'],
        message: 'attendance:configuration.validation.invalidEmailFormat',
      });
    }
  }

  // excessHoursEmail: formato válido si no está vacío
  if (data.excessHoursEmail.trim() !== '') {
    const emailValidation = emailOptionalSchema.safeParse(data.excessHoursEmail);
    if (!emailValidation.success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['excessHoursEmail'],
        message: 'attendance:configuration.validation.invalidEmailFormat',
      });
    }
  }

  // dtFiscalizationEmails: uno o más emails separados por ";"
  if (data.dtFiscalizationEmails.trim() !== '') {
    const emails = data.dtFiscalizationEmails.split(';').map((e) => e.trim()).filter(Boolean);
    const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    const allValid = emails.every((email) => EMAIL_REGEX.test(email) && email.length <= 100);
    if (!allValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dtFiscalizationEmails'],
        message: 'attendance:configuration.validation.invalidMultipleEmailFormat',
      });
    }
  }
});

export type AttendanceConfigurationFormValues = z.infer<typeof attendanceConfigurationSchema>;

/** Shared form prop interface for all attendance configuration sub-forms */
export interface AttendanceConfigForm {
  register: UseFormRegister<AttendanceConfigurationFormValues>;
  watch: UseFormWatch<AttendanceConfigurationFormValues>;
  setValue: UseFormSetValue<AttendanceConfigurationFormValues>;
  errors: FieldErrors<AttendanceConfigurationFormValues>;
}
